import { useMemo, useState } from "react";
import { addDays, format, startOfDay } from "date-fns";
import { z } from "zod";
import {
  Box,
  WidgetEmpty,
  WidgetSkeleton,
  ChartFrame,
  ChartText,
  ChartRampLegend,
  ChartTooltipCard,
  ChartGroup,
  HitRect,
  HeatmapRect,
  localPoint,
  useHeatmapColorScale,
  useHeatmapOpacityScale,
  scaleLinear,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { useActivityHeatmap, type HeatColumn } from "./use-activity-heatmap";
import { at } from "#app/lib/utils/at";

/** A hovered day cell, with the svg-relative pointer position for the tooltip. */
type Hover = { x: number; y: number; title: string; detail: string };

/**
 * `activity-heatmap` — a GitHub-style calendar heatmap of when projects were created
 * or last touched (the heatmap family). Pressure-tests `HeatmapRect` fed a client-built
 * week × day matrix, with sequential single-hue color/opacity encodings. Settings
 * switch the timestamp, the visible window, the color vs opacity encoding, the scale
 * shape (linear vs sqrt for skew), the cell gap, and day labels.
 */
export const activityHeatmapConfigSchema = z
  .looseObject({
    metric: z.enum(["created", "updated"]).default("created"),
    weeks: z.int().min(4).max(26).default(12),
    encode: z.enum(["color", "opacity", "both"]).default("both"),
    intensity: z.enum(["linear", "sqrt"]).default("linear"),
    gap: z.int().min(0).max(6).default(2),
    dayLabels: z.enum(["side", "top", "hidden"]).default("side"),
  });

export type ActivityHeatmapConfig = z.infer<typeof activityHeatmapConfigSchema>;

const bins = (d: HeatColumn) => d.bins;
const count = (d: { count: number }) => d.count;
const MONTH_LABEL_GUTTER = 14;

function weekContainsMonthStart(column: HeatColumn) {
  const firstOfMonth = column.bins.find((bin) => bin.date.getDate() === 1);
  return firstOfMonth ? format(firstOfMonth.date, "MMM") : null;
}

function ActivityHeatmapRender({ h, config }: WidgetRenderContext<ActivityHeatmapConfig>) {
  const { columns, max, dayLabels, loading, error } = useActivityHeatmap(config.metric, config.weeks);
  const [hover, setHover] = useState<Hover | null>(null);
  // Click-through: a day cell opens this card's Data drawer filtered to that day.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  // Sequential single-hue encoding: the graded scale carries the magnitude;
  // the constant shape pins the full hue when opacity alone encodes it.
  const colorVar = useHeatmapColorScale(max, { shape: config.intensity });
  const colorConst = useHeatmapColorScale(max, { shape: "constant" });
  const opacityScaleVar = useHeatmapOpacityScale(max);
  const colorScale = config.encode === "opacity" ? colorConst : colorVar;
  const opacityScale = config.encode === "color" ? undefined : opacityScaleVar;
  const isTopLabels = config.dayLabels === "top";
  const today = startOfDay(new Date()).getTime();
  const transposedColumns = useMemo<HeatColumn[]>(
    () =>
      dayLabels.map((_, dayIndex) => ({
        bin: dayIndex,
        bins: [...columns].reverse().map((column, weekIndex) => ({
          ...at(column.bins, dayIndex, "heat bin"),
          bin: weekIndex,
        })),
      })),
    [columns, dayLabels],
  );
  const displayWeeks = useMemo(
    () => (isTopLabels ? [...columns].reverse() : columns),
    [columns, isTopLabels],
  );
  const weekRows = useMemo(
    () =>
      displayWeeks.map((column, index) => {
        const weekStart = startOfDay(column.bins[0]?.date ?? new Date());
        const weekEnd = addDays(weekStart, 7);
        const total = column.bins.reduce((sum, bin) => sum + bin.count, 0);
        const isThisWeek = today >= weekStart.getTime() && today < weekEnd.getTime();
        return {
          index,
          column,
          weekStart,
          total,
          label: isThisWeek ? "This week" : weekContainsMonthStart(column),
        };
      }),
    [displayWeeks, today],
  );
  const monthLabels = useMemo(
    () =>
      columns
        .map((column, i) => ({ label: weekContainsMonthStart(column), index: i }))
        .filter((item): item is { label: string; index: number } => item.label != null),
    [columns],
  );
  const legendSwatches = useMemo(
    () =>
      [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
        const value = Math.max(1, max * ratio);
        return {
          color: colorScale(value),
          opacity: opacityScale?.(value) ?? 1,
        };
      }),
    [max, colorScale, opacityScale],
  );
  const leftPad =
    config.dayLabels === "side" ? 26 : isTopLabels ? MONTH_LABEL_GUTTER + 20 : 4;
  const topPad = MONTH_LABEL_GUTTER + 4;
  const showLegend = h >= 3;

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="rows" />;
  }
  if (columns.length === 0 || max === 0) {
    return <WidgetEmpty icon="calendar" title="No activity in range" />;
  }

  return (
    <Box direction="column" fill="flex" position="relative">
      <Box direction="column" fill="flex" gap="2xs">
        <Box flex={1}>
          <ChartFrame
            margin={{ top: topPad, right: 4, bottom: 4, left: leftPad }}
            ariaLabel="Project activity heatmap"
          >
            {({ innerWidth, innerHeight }) => {
              if (innerWidth <= 0 || innerHeight <= 0) return null;
              const data = isTopLabels ? transposedColumns : columns;
              const xBins = isTopLabels ? 7 : columns.length;
              const yBins = isTopLabels ? columns.length : 7;
              const xScale = scaleLinear({ domain: [0, xBins], range: [0, innerWidth] });
              const yScale = scaleLinear({ domain: [0, yBins], range: [0, innerHeight] });
              const binWidth = innerWidth / xBins;
              const binHeight = innerHeight / yBins;
              const cellAtPoint = (x: number, y: number) => {
                const col = Math.floor((x - leftPad) / binWidth);
                const row = Math.floor((y - topPad) / binHeight);
                return isTopLabels ? transposedColumns[col]?.bins[row] : columns[col]?.bins[row];
              };
              const weekAtPoint = (x: number, y: number) => {
                const plotX = x - leftPad;
                const plotY = y - topPad;
                if (isTopLabels) {
                  if (plotX >= 0 || plotY < 0 || plotY >= innerHeight) return null;
                  return weekRows[Math.floor(plotY / binHeight)] ?? null;
                }
                if (plotY >= 0 || plotX < 0 || plotX >= innerWidth) return null;
                const column = columns[Math.floor(plotX / binWidth)];
                if (!column) return null;
                const weekStart = startOfDay(column.bins[0]?.date ?? new Date());
                return {
                  weekStart,
                  total: column.bins.reduce((sum, bin) => sum + bin.count, 0),
                };
              };
              const openCell = (cell: { date: Date; count: number }) => {
                if (!drill || !instance || cell.count === 0) return;
                const dayStart = startOfDay(cell.date);
                drill.openData(instance.instanceId, {
                  kind: "dateRange",
                  dateField: config.metric === "updated" ? "lastUpdateDate" : "creationDate",
                  startMs: dayStart.getTime(),
                  endMs: addDays(dayStart, 1).getTime(),
                  label: format(cell.date, "EEE, MMM d"),
                });
              };
              const openWeek = (week: { weekStart: Date; total: number }) => {
                if (!drill || !instance || week.total === 0) return;
                const label = `Week of ${format(week.weekStart, "MMM d")}`;
                drill.openData(instance.instanceId, {
                  kind: "dateRange",
                  dateField: config.metric === "updated" ? "lastUpdateDate" : "creationDate",
                  startMs: week.weekStart.getTime(),
                  endMs: addDays(week.weekStart, 7).getTime(),
                  label,
                });
              };
              return (
                <>
                  {/* One handler over the whole grid: the cell under the pointer comes
                      from the uniform pitch (svg point - margin, divided by bin size). */}
                  <ChartGroup
                    cursor={drill && instance ? "pointer" : undefined}
                    onMouseMove={(e) => {
                      const p = localPoint(e);
                      if (!p) return;
                      const week = weekAtPoint(p.x, p.y);
                      if (week) {
                        setHover({
                          x: p.x,
                          y: p.y,
                          title: `Week of ${format(week.weekStart, "MMM d")}`,
                          detail: `${week.total} ${
                            week.total === 1 ? "project" : "projects"
                          }`,
                        });
                        return;
                      }
                      const cell = cellAtPoint(p.x, p.y);
                      if (!cell) {
                        setHover(null);
                        return;
                      }
                      setHover({
                        x: p.x,
                        y: p.y,
                        title: format(cell.date, "EEE, MMM d"),
                        detail: `${cell.count} ${cell.count === 1 ? "project" : "projects"}`,
                      });
                    }}
                    onMouseLeave={() => setHover(null)}
                    onClick={
                      drill && instance
                        ? (e) => {
                            const p = localPoint(e);
                            if (!p) return;
                            const week = weekAtPoint(p.x, p.y);
                            if (week) {
                              openWeek(week);
                              return;
                            }
                            const cell = cellAtPoint(p.x, p.y);
                            if (cell) openCell(cell);
                          }
                        : undefined
                    }
                  >
                    <HitRect
                      x={-leftPad}
                      y={-topPad}
                      width={innerWidth + leftPad + 4}
                      height={innerHeight + topPad + 4}
                    />
                    <HeatmapRect
                      data={data}
                      xScale={(i: number) => xScale(i) ?? 0}
                      yScale={(i: number) => yScale(i) ?? 0}
                      colorScale={colorScale}
                      opacityScale={opacityScale}
                      binWidth={binWidth}
                      binHeight={binHeight}
                      bins={bins}
                      count={count}
                      gap={config.gap}
                      isCellOutlined={(cell) =>
                        startOfDay(cell.bin.date).getTime() === today
                      }
                    />
                  </ChartGroup>
                  {isTopLabels
                    ? dayLabels.map((label, i) => (
                        <ChartText
                          key={label}
                          x={xScale(i) + binWidth / 2}
                          y={-5}
                          textAnchor="middle"
                          verticalAnchor="end"
                          fontSize={9}
                          colorToken={i === 0 || i === 6 ? "muted" : undefined}
                        >
                          {label}
                        </ChartText>
                      ))
                    : null}
                  {config.dayLabels === "side"
                    ? dayLabels.map((label, i) =>
                        i % 2 === 1 ? (
                          <ChartText
                            key={label}
                            x={-6}
                            y={yScale(i) + binHeight / 2}
                            textAnchor="end"
                            verticalAnchor="middle"
                            fontSize={9}
                            colorToken={i === 0 || i === 6 ? "muted" : undefined}
                          >
                            {label}
                          </ChartText>
                        ) : null,
                      )
                    : null}
                  {isTopLabels
                    ? weekRows.map(({ label, index }) =>
                        label ? (
                          <ChartText
                            key={`${label}-${index}`}
                            x={-6}
                            y={yScale(index) + binHeight / 2}
                            textAnchor="end"
                            verticalAnchor="middle"
                            fontSize={9}
                            colorToken="muted"
                          >
                            {label}
                          </ChartText>
                        ) : null,
                      )
                    : monthLabels.map(({ label, index }) => (
                        <ChartText
                          key={`${label}-${index}`}
                          x={xScale(index) + binWidth / 2}
                          y={-5}
                          textAnchor="middle"
                          verticalAnchor="end"
                          fontSize={9}
                          colorToken="muted"
                        >
                          {label}
                        </ChartText>
                      ))}
                </>
              );
            }}
          </ChartFrame>
        </Box>
        {showLegend ? (
          <ChartRampLegend swatches={legendSwatches} startLabel="Less" endLabel="More" />
        ) : null}
      </Box>
      {hover ? (
        <ChartTooltipCard left={hover.x} top={hover.y}>
          <WidgetChartTooltip label={hover.title} details={hover.detail} />
        </ChartTooltipCard>
      ) : null}
    </Box>
  );
}

export const activityHeatmapDef: WidgetModule<ActivityHeatmapConfig> = {
  type: "activity-heatmap",
  title: "Activity heatmap",
  icon: "calendar",
  span: { minW: 4, minH: 2, maxW: 12, maxH: 8, defaultW: 6, defaultH: 3 },
  configSchema: activityHeatmapConfigSchema,
  headerActions: WidgetHeaderFilters,
  settings: [
    {
      title: "Range",
      fields: [
        {
          key: "metric",
          kind: "segmented",
          label: "Timestamp",
          options: [
            { value: "created", label: "Created" },
            { value: "updated", label: "Updated" },
          ],
        },
        { key: "weeks", kind: "number", label: "Weeks", min: 4, max: 26, step: 1 },
      ],
    },
    {
      title: "Color",
      fields: [
        {
          key: "encode",
          kind: "segmented",
          label: "Encode by",
          options: [
            { value: "color", label: "Color" },
            { value: "opacity", label: "Opacity" },
            { value: "both", label: "Both" },
          ],
        },
        {
          key: "intensity",
          kind: "segmented",
          label: "Scale",
          options: [
            { value: "linear", label: "Linear" },
            { value: "sqrt", label: "Sqrt" },
          ],
        },
      ],
    },
    {
      title: "Cells",
      fields: [
        { key: "gap", kind: "number", label: "Cell gap", min: 0, max: 6, step: 1 },
        {
          key: "dayLabels",
          kind: "segmented",
          label: "Day labels",
          options: [
            { value: "side", label: "Side" },
            { value: "top", label: "Top" },
            { value: "hidden", label: "Hidden" },
          ],
        },
      ],
    },
  ],
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "activity-heatmap.workflow-activity",
      label: "Workflow activity",
      description: "See when work is created or updated across the workflow.",
      section: "Workflow Dashboard",
      tags: ["projects", "activity"] satisfies readonly WidgetTag[],
      defaultConfig: { metric: "updated", weeks: 12, encode: "both" },
      defaultW: 6,
      defaultH: 3,
    },
    {
      key: "activity-heatmap.default",
      label: "Activity heatmap",
      description: "When projects are created or updated, as a week × day calendar heatmap.",
      section: "Charts",
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: { metric: "created", weeks: 12, encode: "both" },
      defaultW: 6,
      defaultH: 3,
    },
  ],
  render: ActivityHeatmapRender,
};
